import { ReactNode, useEffect, useState } from "react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/site/PageHeader";
import { RichText } from "@/components/site/RichText";
import { readBodyFormat } from "@/lib/rich-body";
import { pageDraftQueryOptions, pageQueryOptions, type SitePage } from "@/lib/pages.functions";
import { BuilderBlocks } from "@/components/admin/page-builder/renderBuilderBlocks";
import type { Block } from "@/components/admin/page-builder/types";

type Defaults = {
  eyebrow?: string;
  title: string;
  lead?: string;
};

/** True when the current URL has ?preview=1 (admin preview mode). */
export function useIsPreviewMode(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () =>
      setOn(new URLSearchParams(window.location.search).get("preview") === "1");
    update();
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return on;
}

/**
 * Returns the live page row by default. In admin preview mode (?preview=1)
 * the draft is fetched and merged so the page renders exactly as it would
 * look after publishing.
 */
export function useSitePage(slug: string): SitePage | null {
  const { data: live } = useSuspenseQuery(pageQueryOptions(slug));
  const isPreview = useIsPreviewMode();
  const draftQuery = useQuery({
    ...pageDraftQueryOptions(slug),
    enabled: isPreview,
  });
  if (isPreview && draftQuery.data) return draftQuery.data;
  return live;
}

/**
 * Wraps a page so its hero (eyebrow/title/lead) and body can be overridden from
 * the admin Pages tab. When `use_custom_body` is true on the page row, the
 * children are replaced by the rendered markdown body and optional hero image.
 */
export function EditablePageShell({
  slug,
  defaults,
  children,
  hideHeader,
}: {
  slug: string;
  defaults: Defaults;
  children: ReactNode;
  hideHeader?: boolean;
}) {
  const page = useSitePage(slug);
  const isPreview = useIsPreviewMode();
  const eyebrow = page?.hero_eyebrow?.trim() || defaults.eyebrow;
  const title = page?.hero_title?.trim() || defaults.title;
  const lead = page?.hero_lead?.trim() || defaults.lead;

  const builderBlocks = readBuilderBlocks((page?.extra as any)?.builder);
  const hasBuilder = builderBlocks.length > 0;
  const useCustom = page?.use_custom_body === true && (page.body_md ?? "").trim().length > 0;
  const galleryImages = normalizePageImages((page?.extra as any)?.images);
  const heroGallery = galleryImages.filter((i) => i.placement === "hero" && i.url);
  const bodyGallery = galleryImages.filter((i) => i.placement === "body" && i.url);

  return (
    <>
      {isPreview && <PreviewBanner />}
      {!hideHeader && <PageHeader eyebrow={eyebrow} title={title} lead={lead} />}
      {hasBuilder ? (
        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <BuilderBlocks blocks={builderBlocks} />
        </section>
      ) : useCustom ? (
        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          {heroGallery.length > 0 ? (
            <div className="mb-8 space-y-4">
              {heroGallery.map((img) => (
                <Figure key={img.id} img={img} className="rounded-3xl" />
              ))}
            </div>
          ) : page?.hero_image_url ? (
            <div className="mb-8 overflow-hidden rounded-3xl border border-border bg-card">
              <img src={page.hero_image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          ) : null}
          <RichText markdown={page!.body_md} format={readBodyFormat(page!.extra)} />
          {bodyGallery.length > 0 && (
            <div className="mt-8 space-y-4">
              {bodyGallery.map((img) => (
                <Figure key={img.id} img={img} className="rounded-2xl" />
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          {children}
          {(heroGallery.length > 0 || bodyGallery.length > 0) && (
            <section className="mx-auto max-w-3xl space-y-4 px-4 py-12 sm:px-6 lg:px-8">
              {[...heroGallery, ...bodyGallery].map((img) => (
                <Figure key={img.id} img={img} className="rounded-2xl" />
              ))}
            </section>
          )}
        </>
      )}
    </>
  );
}

type ShellImage = { id: string; url: string; alt: string; caption?: string; placement: "hero" | "body" };

export function readBuilderBlocks(raw: unknown): Block[] {
  if (!raw || typeof raw !== "object") return [];
  const blocks = (raw as any).blocks;
  if (!Array.isArray(blocks)) return [];
  return blocks.filter((b) => b && typeof b === "object" && typeof b.id === "string" && typeof b.type === "string") as Block[];
}

function normalizePageImages(raw: unknown): ShellImage[] {
  if (!Array.isArray(raw)) return [];
  const out: ShellImage[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const url = typeof (item as any).url === "string" ? (item as any).url : "";
    if (!url) continue;
    out.push({
      id: typeof (item as any).id === "string" ? (item as any).id : url,
      url,
      alt: typeof (item as any).alt === "string" ? (item as any).alt : "",
      caption: typeof (item as any).caption === "string" ? (item as any).caption : "",
      placement: (item as any).placement === "body" ? "body" : "hero",
    });
  }
  return out;
}

function Figure({ img, className }: { img: ShellImage; className?: string }) {
  return (
    <figure className={`overflow-hidden border border-border bg-card ${className ?? ""}`}>
      <img src={img.url} alt={img.alt} className="h-full w-full object-cover" loading="lazy" />
      {img.caption && (
        <figcaption className="px-4 py-2 text-center text-xs text-muted-foreground">
          {img.caption}
        </figcaption>
      )}
    </figure>
  );
}

function PreviewBanner() {
  function exitPreview() {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    window.location.replace(url.toString());
  }
  return (
    <div className="sticky top-0 z-50 flex items-center justify-center gap-3 bg-primary px-4 py-2 text-center text-xs font-semibold text-primary-foreground shadow">
      <span>Preview mode — showing unpublished draft</span>
      <button
        onClick={exitPreview}
        className="rounded-full border border-primary-foreground/40 px-3 py-0.5 text-[11px] font-medium hover:bg-primary-foreground/10"
      >
        Exit preview
      </button>
    </div>
  );
}
